import { Link } from 'react-router-dom';

/**
 * Datenschutzerklärung (AP-B6).
 *
 * Editorial-Stil konsistent mit dem Rest der App. Eine fokussierte Seite
 * mit allen DSGVO-Pflichtangaben — keine 30-seitige Anwaltsprosa, sondern
 * ein behördentauglicher, lesbarer Text in der Sie-Form.
 *
 * Stand: 2026-05-14. Bei Änderungen am Verarbeitungs-Setup (z.B. neue Sub-
 * Auftragsverarbeiter) MUSS diese Seite aktualisiert UND die Versions-Zeile
 * unten erhöht werden.
 *
 * Alle Texte stehen direkt in der Komponente — bewusst NICHT in i18n/de.ts
 * ausgelagert, weil rechtliche Texte nicht über die übliche String-Sammlung
 * laufen sollten. Änderungen brauchen Review durch Juristen.
 */
export function Datenschutz() {
  return (
    <article className="pb-24 pt-12 md:pt-20">
      <header className="relative pb-10">
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 hidden h-24 w-24 rounded-sm md:block"
          style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
        />
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Information
        </p>
        <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
          DATENSCHUTZ.
        </h1>
        <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:text-lg">
          Wie wir mit Ihren Sozial- und Personendaten umgehen — nüchtern und vollständig.
        </p>
      </header>

      <div className="prose prose-stone max-w-prose font-serif text-base leading-relaxed text-stone-700">
        <Section title="1 · Verantwortlicher">
          <p>Verantwortlich für die Datenverarbeitung im Sinne von Art. 4 Nr. 7 DSGVO ist:</p>
          <Address>
            Petschwork Consulting GmbH
            <br />
            vertreten durch Dr. Stefanie Petsch-van Randenborgh
            <br />
            Pettenkoferstr. 4
            <br />
            80336 München
            <br />
            Tel. 089/54884586-0
            <br />
            <a href="mailto:datenschutz@petschwork.de" className="underline">
              datenschutz@petschwork.de
            </a>
          </Address>
          <p>
            Ein:e Datenschutzbeauftragte:r ist nicht bestellt, da die gesetzlichen Schwellenwerte (§
            38 BDSG) nicht überschritten werden. Datenschutzanfragen erreichen uns unter der oben
            genannten E-Mail-Adresse.
          </p>
        </Section>

        <Section title="2 · Hosting und Datenstandort">
          <p>
            Die Anwendung läuft auf <strong>Cloudflare Pages</strong> (EU-Edge-Standorte) und
            verwendet als Datenbank- und Auth-Backend <strong>Supabase</strong> mit Hosting in
            Frankfurt am Main (Region eu-central-1). Beide Anbieter sind gemäß Art. 28 DSGVO als
            Auftragsverarbeiter eingebunden; die Auftragsverarbeitungs-Verträge liegen vor.
          </p>
          <p>
            Es findet <strong>keine</strong> Datenübermittlung in Drittstaaten außerhalb der EU oder
            des EWR statt. Es werden keine Schriftarten von US-CDNs (Google Fonts) oder
            Drittanbieter-Analyse-Werkzeuge eingebunden.
          </p>
        </Section>

        <Section title="3 · Erhobene Daten">
          <p>
            Die Tragfähigkeits-App verarbeitet folgende Kategorien personenbezogener Daten —
            ausschließlich, soweit Sie sie selbst eingeben:
          </p>
          <ul>
            <li>
              <strong>Stammdaten:</strong> Name, optional Geburtsdatum und Adresse, sofern für die
              Stellungnahme erforderlich.
            </li>
            <li>
              <strong>Kontaktdaten:</strong> E-Mail-Adresse für die Anmeldung per Magic-Link.
            </li>
            <li>
              <strong>Mandatsdaten:</strong> Förderkontext (z.B. § 10 SGB II), Jobcenter-Bezug bei
              B2G-Mandaten, Aktenzeichen.
            </li>
            <li>
              <strong>Wirtschaftliche Daten:</strong> BWA-/EKS-Eckdaten, Liquiditätsangaben,
              Stundensatz, Zusammensetzung der Bedarfsgemeinschaft. Diese sind sensible Sozialdaten
              nach § 35 SGB I.
            </li>
            <li>
              <strong>Bewertungstexte und Stellungnahmen:</strong> Vom Berater oder der Beraterin
              freigegebene Texteinordnung sowie generierte Stellungnahme-Dokumente.
            </li>
            <li>
              <strong>Technische Logs:</strong> Audit-Trail über Zugriffe, IP-Adresse und
              Zeitstempel zur Erfüllung der Nachweispflicht (Art. 5 Abs. 2 DSGVO).
            </li>
          </ul>
          <p>
            <strong>Datenminimierung:</strong> Es werden keine Religionszugehörigkeit, kein
            Familienstand, keine politischen Einstellungen erfasst — auch dann nicht, wenn das
            Jobcenter sie übermittelt. Felder, die nicht für die Stellungnahme nötig sind, bleiben
            leer.
          </p>
        </Section>

        <Section title="4 · Zwecke und Rechtsgrundlagen">
          <p>Die Verarbeitung erfolgt zu folgenden Zwecken auf folgenden Rechtsgrundlagen:</p>
          <ul>
            <li>
              <strong>Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO):</strong> Vorbereitung und
              Erstellung der Tragfähigkeitsstellungnahme für Sie als Mandant:in (B2C) oder im
              Auftrag des Jobcenters (B2G).
            </li>
            <li>
              <strong>Rechtliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO):</strong> Aufbewahrung
              von Stellungnahmen, die zu einer behördlichen Entscheidung beigetragen haben.
            </li>
            <li>
              <strong>§ 67c SGB X (B2G):</strong> Sozialdaten dürfen für den ursprünglichen Zweck
              der Erhebung verarbeitet werden — hier: Tragfähigkeitsprüfung nach § 10 Abs. 2 Nr. 5
              SGB II.
            </li>
            <li>
              <strong>Berechtigtes Interesse (Art. 6 Abs. 1 lit. f DSGVO):</strong> Audit-Logging
              zur Nachweisführung. Eine Profilbildung oder Werbung erfolgt nicht.
            </li>
          </ul>
          <p>
            Sensible Daten nach Art. 9 DSGVO (insbesondere gesundheitsbezogene Angaben) verarbeiten
            wir auf Grundlage von Art. 9 Abs. 2 lit. b DSGVO i.V.m. § 67c SGB X — soweit Sie diese
            Angaben selbst zur Beurteilung Ihrer Belastbarkeit machen.
          </p>
        </Section>

        <Section title="5 · Speicherdauer">
          <p>
            <strong>B2C-Mandate (Vorbereitung durch Mandant:in):</strong> Aufbewahrung 24 Monate
            nach Abschluss des Mandats. Danach automatische Löschung durch einen technisch
            erzwungenen Cron-Job.
          </p>
          <p>
            <strong>B2G-Mandate (Auftrag eines Jobcenters):</strong> Aufbewahrung 10 Jahre. Diese
            Frist ergibt sich aus den Aufbewahrungspflichten der beteiligten Sozialleistungsträger
            sowie aus § 84 SGB X.
          </p>
          <p>
            <strong>Audit-Logs:</strong> Aufbewahrung 10 Jahre. Danach Archivierung auf einem
            EU-Glacier-Speicher mit eingeschränktem Zugriff.
          </p>
          <p>
            <strong>BWA-/EKS-Rohuploads:</strong> Aufbewahrung 12 Monate nach Abschluss. Danach
            Löschung der Rohdatei; die daraus errechneten Kennzahlen bleiben in anonymisierter Form
            Bestandteil der Stellungnahme.
          </p>
        </Section>

        <Section title="6 · Empfänger der Daten">
          <p>Wir geben Daten nur an folgende Stellen weiter:</p>
          <ul>
            <li>
              <strong>Auftragsverarbeiter</strong> (Cloudflare Inc., Supabase Inc.) — beide
              vertraglich nach Art. 28 DSGVO gebunden, EU-Verarbeitung garantiert.
            </li>
            <li>
              <strong>Das beauftragende Jobcenter</strong> (nur bei B2G-Mandaten) — und auch dort
              nur die finale Stellungnahme nach Ihrer expliziten Freigabe.
            </li>
            <li>
              <strong>Steuerberatung und Rechtsbeistand</strong> — nur soweit gesetzlich oder
              vertraglich erforderlich.
            </li>
          </ul>
          <p>
            Eine Übermittlung an sonstige Dritte oder eine Weitergabe zu Werbezwecken findet nicht
            statt.
          </p>
        </Section>

        <Section title="7 · Cookies und lokaler Speicher">
          <p>
            Die Anwendung setzt ausschließlich technisch notwendige Cookies und nutzt den lokalen
            Browser-Speicher (IndexedDB) zur Sitzungs- und Wizard-Pflege. Es werden keine Tracking-
            oder Marketing-Cookies, keine Analyse-Pixel und keine Drittanbieter-Skripte geladen.
          </p>
        </Section>

        <Section title="8 · Ihre Rechte">
          <p>Nach DSGVO stehen Ihnen folgende Rechte zu:</p>
          <ul>
            <li>
              <strong>Auskunft</strong> (Art. 15 DSGVO) — welche Daten wir über Sie verarbeiten.
            </li>
            <li>
              <strong>Berichtigung</strong> (Art. 16 DSGVO) — wenn Daten unrichtig sind.
            </li>
            <li>
              <strong>Löschung</strong> (Art. 17 DSGVO) — soweit keine gesetzliche
              Aufbewahrungspflicht entgegensteht.
            </li>
            <li>
              <strong>Einschränkung</strong> (Art. 18 DSGVO) der Verarbeitung.
            </li>
            <li>
              <strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO) in einem strukturierten,
              gängigen Format.
            </li>
            <li>
              <strong>Widerspruch</strong> (Art. 21 DSGVO) gegen Verarbeitungen auf Basis
              berechtigten Interesses.
            </li>
            <li>
              <strong>Kein Profiling, keine automatisierte Einzelfallentscheidung</strong> (Art. 22
              DSGVO) — der erzeugte Tragfähigkeits-Score ist Entscheidungs<em>vorbereitung</em>; die
              Stellungnahme zeichnet immer ein:e Berater:in persönlich.
            </li>
          </ul>
          <p>Ihre Anfragen richten Sie bitte an:</p>
          <Address>
            Petschwork Consulting GmbH
            <br />
            Datenschutz
            <br />
            Pettenkoferstr. 4
            <br />
            80336 München
            <br />
            <a href="mailto:datenschutz@petschwork.de" className="underline">
              datenschutz@petschwork.de
            </a>
          </Address>
          <p>
            Sie haben darüber hinaus das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu
            beschweren — in Bayern bei dem Bayerischen Landesbeauftragten für den Datenschutz
            (BayLfD), Wagmüllerstraße 18, 80538 München.
          </p>
        </Section>

        <Section title="9 · Sicherheit der Verarbeitung">
          <p>
            Die Übertragung erfolgt durchgängig per TLS 1.3, die Speicherung mit AES-256 at rest.
            Sensible Felder (BWA-Rohwerte, Gesundheitsangaben) werden zusätzlich auf Anwendungsebene
            per libsodium verschlüsselt. Zugriffe werden im Audit-Log protokolliert (Art. 32 DSGVO).
          </p>
          <p>
            Die Datenbank ist mit Row-Level-Security gehärtet: Nutzer:innen sehen ausschließlich
            ihre eigenen Mandate. Beratende Personen sehen Mandate ihres Tenants. Cross-Tenant-
            Zugriffe sind technisch ausgeschlossen.
          </p>
        </Section>

        <Section title="10 · Stand und Änderungen">
          <p className="text-sm italic text-stone-500">
            Diese Datenschutzerklärung gilt in der Fassung vom <strong>14. Mai 2026</strong>.
            Wesentliche Änderungen werden Ihnen vor dem nächsten Login angezeigt. Frühere Fassungen
            stellen wir auf Anfrage zur Verfügung.
          </p>
        </Section>

        <p className="mt-12 border-t border-stone-200 pt-6 font-sans text-xs uppercase tracking-widest text-stone-500">
          Siehe auch:{' '}
          <Link to="/impressum" className="underline hover:text-stone-700">
            Impressum
          </Link>
        </p>
      </div>
    </article>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="mt-10 first:mt-0">
      <h2 className="mb-3 font-serif text-xl font-bold tracking-tight text-stone-900">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Address({ children }: { children: React.ReactNode }) {
  return (
    <address className="not-italic rounded-sm border-l-2 border-stone-200 pl-4 font-serif text-base text-stone-700">
      {children}
    </address>
  );
}
